import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import ReactGA from 'react-ga4';

const AdminDashboard = ({ user }) => {
  const [allActivities, setAllActivities] = useState([]);
  const [activities, setActivities] = useState([]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // overview, users, videos, activity
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!user || !user.email) {
      setError('User not authenticated');
      setLoading(false);
      return;
    }

    loadData();
  }, [user, activeTab]);

  // Filter activities client-side
  useEffect(() => {
    if (allActivities.length === 0) return;

    let filtered = [...allActivities];

    if (filter !== 'all') {
      filtered = filtered.filter(a => a.action === filter);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(a => 
        (a.user_email && a.user_email.toLowerCase().includes(query)) ||
        (a.video_title && a.video_title.toLowerCase().includes(query)) ||
        (a.video_url && a.video_url.toLowerCase().includes(query)) ||
        (a.username && a.username.toLowerCase().includes(query))
      );
    }

    setActivities(filtered);
  }, [allActivities, filter, searchQuery]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const promises = [
        apiService.getAdminStats(user.email),
        apiService.getAdminActivity(user.email),
        apiService.getAdminUsers(user.email),
        apiService.getAdminVideos(user.email)
      ];

      const [statsResponse, activityResponse, usersResponse, videosResponse] = await Promise.all(promises);

      if (statsResponse.success) {
        setStats(statsResponse.stats);
      }

      if (activityResponse.success) {
        setAllActivities(activityResponse.activities || []);
      }

      if (usersResponse.success) {
        setUsers(usersResponse.users || []);
      }

      if (videosResponse.success) {
        setVideos(videosResponse.videos || []);
      }

      ReactGA.event({
        category: 'Admin',
        action: 'Dashboard Accessed',
        label: 'admin_dashboard'
      });
    } catch (err) {
      console.error('Error loading admin data:', err);
      setError(err.response?.data?.detail || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '—';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return timestamp;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'video_analyzed':
        return '#8b5cf6';
      case 'channel_analyzed':
        return '#ec4899';
      case 'comments_analyzed':
        return '#06b6d4';
      default:
        return '#9ca3af';
    }
  };

  const getActionLabel = (action) => {
    switch (action) {
      case 'video_analyzed':
        return 'Video Analyzed';
      case 'channel_analyzed':
        return 'Channel Analyzed';
      case 'comments_analyzed':
        return 'Comments Analyzed';
      default:
        return action;
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px 20px 40px',
        background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a3a 100%)'
      }}>
        <div style={{ textAlign: 'center', color: '#e5e7eb' }}>
          <div className="spinner" style={{ marginBottom: '1rem' }} />
          <p>Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px 20px 40px',
        background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a3a 100%)'
      }}>
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '2px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '16px',
          padding: '2rem',
          maxWidth: '500px',
          textAlign: 'center'
        }}>
          <h2 style={{ color: '#ef4444', marginBottom: '1rem' }}>Access Denied</h2>
          <p style={{ color: '#e5e7eb' }}>{error}</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'users', label: `Users (${users.length})` },
    { id: 'videos', label: `Videos (${videos.length})` },
    { id: 'activity', label: `Activity Log (${activities.length})` }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      padding: '100px 20px 40px',
      background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a3a 100%)'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{
            fontSize: '2.5rem',
            fontWeight: 'bold',
            background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '0.5rem'
          }}>
            Admin Dashboard
          </h1>
          <p style={{ color: '#9ca3af', fontSize: '1.1rem' }}>
            Monitor user activity and system statistics
          </p>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          marginBottom: '2rem',
          borderBottom: '2px solid rgba(139, 92, 246, 0.3)',
          paddingBottom: '0.5rem'
        }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '0.75rem 1.5rem',
                background: activeTab === tab.id 
                  ? 'linear-gradient(135deg, #8b5cf6, #ec4899)' 
                  : 'transparent',
                border: 'none',
                borderRadius: '12px 12px 0 0',
                color: activeTab === tab.id ? '#fff' : '#9ca3af',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '1rem',
                transition: 'all 0.2s ease',
                borderBottom: activeTab === tab.id ? '3px solid transparent' : 'none'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '1.5rem',
              marginBottom: '2rem'
            }}>
              <div style={{
                background: 'rgba(15, 15, 35, 0.92)',
                border: '2px solid rgba(139, 92, 246, 0.3)',
                borderRadius: '20px',
                padding: '1.5rem',
                boxShadow: '0 18px 45px rgba(0,0,0,0.4)'
              }}>
                <div style={{ color: '#9ca3af', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                  Total Activities
                </div>
                <div style={{ color: '#8b5cf6', fontSize: '2rem', fontWeight: 'bold' }}>
                  {stats.total_activities || 0}
                </div>
              </div>

              <div style={{
                background: 'rgba(15, 15, 35, 0.92)',
                border: '2px solid rgba(236, 72, 153, 0.3)',
                borderRadius: '20px',
                padding: '1.5rem',
                boxShadow: '0 18px 45px rgba(0,0,0,0.4)'
              }}>
                <div style={{ color: '#9ca3af', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                  Unique Users
                </div>
                <div style={{ color: '#ec4899', fontSize: '2rem', fontWeight: 'bold' }}>
                  {stats.unique_users || 0}
                </div>
              </div>

              <div style={{
                background: 'rgba(15, 15, 35, 0.92)',
                border: '2px solid rgba(6, 182, 212, 0.3)',
                borderRadius: '20px',
                padding: '1.5rem',
                boxShadow: '0 18px 45px rgba(0,0,0,0.4)'
              }}>
                <div style={{ color: '#9ca3af', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                  Unique Videos
                </div>
                <div style={{ color: '#06b6d4', fontSize: '2rem', fontWeight: 'bold' }}>
                  {stats.unique_videos || 0}
                </div>
              </div>

              <div style={{
                background: 'rgba(15, 15, 35, 0.92)',
                border: '2px solid rgba(139, 92, 246, 0.3)',
                borderRadius: '20px',
                padding: '1.5rem',
                boxShadow: '0 18px 45px rgba(0,0,0,0.4)'
              }}>
                <div style={{ color: '#9ca3af', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                  Last 24 Hours
                </div>
                <div style={{ color: '#8b5cf6', fontSize: '2rem', fontWeight: 'bold' }}>
                  {stats.recent_activities_24h || 0}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div style={{
            background: 'rgba(15, 15, 35, 0.92)',
            border: '2px solid rgba(139, 92, 246, 0.3)',
            borderRadius: '20px',
            padding: '1.5rem',
            boxShadow: '0 18px 45px rgba(0,0,0,0.4)',
            overflowX: 'auto'
          }}>
            <h2 style={{ color: '#e5e7eb', marginBottom: '1.5rem', fontSize: '1.5rem' }}>
              All Users ({users.length})
            </h2>

            {users.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>
                No users found
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid rgba(139, 92, 246, 0.3)' }}>
                      <th style={{ padding: '1rem', textAlign: 'left', color: '#8b5cf6', fontWeight: '600' }}>User</th>
                      <th style={{ padding: '1rem', textAlign: 'left', color: '#8b5cf6', fontWeight: '600' }}>Total Activities</th>
                      <th style={{ padding: '1rem', textAlign: 'left', color: '#8b5cf6', fontWeight: '600' }}>Video Analyses</th>
                      <th style={{ padding: '1rem', textAlign: 'left', color: '#8b5cf6', fontWeight: '600' }}>Videos Analyzed</th>
                      <th style={{ padding: '1rem', textAlign: 'left', color: '#8b5cf6', fontWeight: '600' }}>First Activity</th>
                      <th style={{ padding: '1rem', textAlign: 'left', color: '#8b5cf6', fontWeight: '600' }}>Last Activity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((userData, idx) => (
                      <tr
                        key={idx}
                        style={{
                          borderBottom: '1px solid rgba(139, 92, 246, 0.1)',
                          transition: 'background 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(139, 92, 246, 0.05)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <td style={{ padding: '1rem', color: '#e5e7eb' }}>
                          <div>
                            <div style={{ fontWeight: '500', marginBottom: '0.25rem' }}>
                              {userData.username || userData.email || 'Unknown'}
                            </div>
                            <div style={{ fontSize: '0.85rem', color: '#9ca3af' }}>
                              {userData.email}
                            </div>
                            {userData.user_id && (
                              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                                ID: {userData.user_id}
                              </div>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '1rem', color: '#8b5cf6', fontWeight: '600' }}>
                          {userData.total_activities || 0}
                        </td>
                        <td style={{ padding: '1rem', color: '#ec4899', fontWeight: '600' }}>
                          {userData.video_analyses || 0}
                        </td>
                        <td style={{ padding: '1rem', color: '#06b6d4', fontWeight: '600' }}>
                          {userData.videos_analyzed?.length || 0}
                        </td>
                        <td style={{ padding: '1rem', color: '#9ca3af', fontSize: '0.9rem' }}>
                          {formatDate(userData.first_activity)}
                        </td>
                        <td style={{ padding: '1rem', color: '#9ca3af', fontSize: '0.9rem' }}>
                          {formatDate(userData.last_activity)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Videos Tab */}
        {activeTab === 'videos' && (
          <div style={{
            background: 'rgba(15, 15, 35, 0.92)',
            border: '2px solid rgba(139, 92, 246, 0.3)',
            borderRadius: '20px',
            padding: '1.5rem',
            boxShadow: '0 18px 45px rgba(0,0,0,0.4)',
            overflowX: 'auto'
          }}>
            <h2 style={{ color: '#e5e7eb', marginBottom: '1.5rem', fontSize: '1.5rem' }}>
              All Videos Analyzed ({videos.length})
            </h2>

            {videos.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>
                No videos found
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid rgba(139, 92, 246, 0.3)' }}>
                      <th style={{ padding: '1rem', textAlign: 'left', color: '#8b5cf6', fontWeight: '600' }}>Video</th>
                      <th style={{ padding: '1rem', textAlign: 'left', color: '#8b5cf6', fontWeight: '600' }}>Total Analyses</th>
                      <th style={{ padding: '1rem', textAlign: 'left', color: '#8b5cf6', fontWeight: '600' }}>Unique Users</th>
                      <th style={{ padding: '1rem', textAlign: 'left', color: '#8b5cf6', fontWeight: '600' }}>First Analyzed</th>
                      <th style={{ padding: '1rem', textAlign: 'left', color: '#8b5cf6', fontWeight: '600' }}>Last Analyzed</th>
                      <th style={{ padding: '1rem', textAlign: 'left', color: '#8b5cf6', fontWeight: '600' }}>URL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {videos.map((video, idx) => (
                      <tr
                        key={idx}
                        style={{
                          borderBottom: '1px solid rgba(139, 92, 246, 0.1)',
                          transition: 'background 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(139, 92, 246, 0.05)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <td style={{ padding: '1rem', color: '#e5e7eb', maxWidth: '300px' }}>
                          <div>
                            <div style={{ fontWeight: '500', marginBottom: '0.25rem' }}>
                              {video.video_title || 'Unknown Title'}
                            </div>
                            <div style={{ fontSize: '0.85rem', color: '#9ca3af' }}>
                              ID: {video.video_id}
                            </div>
                            {video.categories && video.categories.length > 0 && (
                              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                                Categories: {video.categories.join(', ')}
                              </div>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '1rem', color: '#8b5cf6', fontWeight: '600' }}>
                          {video.total_analyses || 0}
                        </td>
                        <td style={{ padding: '1rem', color: '#ec4899', fontWeight: '600' }}>
                          {video.unique_users_count || 0}
                        </td>
                        <td style={{ padding: '1rem', color: '#9ca3af', fontSize: '0.9rem' }}>
                          {formatDate(video.first_analyzed)}
                        </td>
                        <td style={{ padding: '1rem', color: '#9ca3af', fontSize: '0.9rem' }}>
                          {formatDate(video.last_analyzed)}
                        </td>
                        <td style={{ padding: '1rem' }}>
                          {video.video_url ? (
                            <a
                              href={video.video_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: '#8b5cf6',
                                textDecoration: 'none',
                                fontSize: '0.9rem',
                                wordBreak: 'break-all'
                              }}
                              onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                              onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                            >
                              {video.video_url}
                            </a>
                          ) : (
                            <span style={{ color: '#9ca3af' }}>—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Activity Log Tab */}
        {activeTab === 'activity' && (
          <div>
            <div style={{
              display: 'flex',
              gap: '1rem',
              marginBottom: '2rem',
              flexWrap: 'wrap',
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {['all', 'video_analyzed', 'channel_analyzed', 'comments_analyzed'].map(action => (
                  <button
                    key={action}
                    onClick={() => {
                      setFilter(action);
                      setSearchQuery('');
                    }}
                    style={{
                      padding: '0.6rem 1.2rem',
                      background: filter === action 
                        ? 'linear-gradient(135deg, #8b5cf6, #ec4899)' 
                        : 'rgba(15, 15, 35, 0.92)',
                      border: `2px solid ${filter === action ? 'transparent' : 'rgba(139, 92, 246, 0.3)'}`,
                      borderRadius: '12px',
                      color: filter === action ? '#fff' : '#e5e7eb',
                      cursor: 'pointer',
                      fontWeight: '500',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {action === 'all' ? 'All' : getActionLabel(action)}
                  </button>
                ))}
              </div>

              <input
                type="text"
                placeholder="Search by email, video title, or URL..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  flex: 1,
                  minWidth: '250px',
                  padding: '0.6rem 1.2rem',
                  background: 'rgba(15, 15, 35, 0.92)',
                  border: '2px solid rgba(139, 92, 246, 0.3)',
                  borderRadius: '12px',
                  color: '#e5e7eb',
                  fontSize: '0.95rem',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{
              background: 'rgba(15, 15, 35, 0.92)',
              border: '2px solid rgba(139, 92, 246, 0.3)',
              borderRadius: '20px',
              padding: '1.5rem',
              boxShadow: '0 18px 45px rgba(0,0,0,0.4)',
              overflowX: 'auto'
            }}>
              <h2 style={{ color: '#e5e7eb', marginBottom: '1.5rem', fontSize: '1.5rem' }}>
                Activity Log ({activities.length})
              </h2>

              {activities.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>
                  No activities found
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid rgba(139, 92, 246, 0.3)' }}>
                        <th style={{ padding: '1rem', textAlign: 'left', color: '#8b5cf6', fontWeight: '600' }}>Timestamp</th>
                        <th style={{ padding: '1rem', textAlign: 'left', color: '#8b5cf6', fontWeight: '600' }}>User</th>
                        <th style={{ padding: '1rem', textAlign: 'left', color: '#8b5cf6', fontWeight: '600' }}>Action</th>
                        <th style={{ padding: '1rem', textAlign: 'left', color: '#8b5cf6', fontWeight: '600' }}>Video/Channel</th>
                        <th style={{ padding: '1rem', textAlign: 'left', color: '#8b5cf6', fontWeight: '600' }}>URL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activities.map((activity, idx) => (
                        <tr
                          key={idx}
                          style={{
                            borderBottom: '1px solid rgba(139, 92, 246, 0.1)',
                            transition: 'background 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(139, 92, 246, 0.05)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          <td style={{ padding: '1rem', color: '#9ca3af', fontSize: '0.9rem' }}>
                            {formatDate(activity.timestamp)}
                          </td>
                          <td style={{ padding: '1rem', color: '#e5e7eb' }}>
                            <div>
                              <div style={{ fontWeight: '500' }}>
                                {activity.username || activity.user_email || 'Unknown'}
                              </div>
                              <div style={{ fontSize: '0.85rem', color: '#9ca3af' }}>
                                {activity.user_email}
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: '1rem' }}>
                            <span style={{
                              padding: '0.4rem 0.8rem',
                              background: `${getActionColor(activity.action)}20`,
                              border: `1px solid ${getActionColor(activity.action)}50`,
                              borderRadius: '8px',
                              color: getActionColor(activity.action),
                              fontSize: '0.85rem',
                              fontWeight: '500'
                            }}>
                              {getActionLabel(activity.action)}
                            </span>
                          </td>
                          <td style={{ padding: '1rem', color: '#e5e7eb', maxWidth: '300px' }}>
                            {activity.video_title || activity.channel_url || '—'}
                          </td>
                          <td style={{ padding: '1rem' }}>
                            {activity.video_url || activity.channel_url ? (
                              <a
                                href={activity.video_url || activity.channel_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: '#8b5cf6',
                                  textDecoration: 'none',
                                  fontSize: '0.9rem',
                                  wordBreak: 'break-all'
                                }}
                                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                              >
                                {activity.video_url || activity.channel_url}
                              </a>
                            ) : (
                              <span style={{ color: '#9ca3af' }}>—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
